import { ButtonPrimitive, CHECKERBOARD_BACKGROUND, Stack, Text } from '@figma/fpl-components';
import { Icon24Refresh, Icon24Upload } from '@figma/fpl-icons';
import cssVars from '@figma/fpl-tokens/css-vars';
import * as stylex from '@stylexjs/stylex';
import { useTranslation } from 'react-i18next';
import { LinearProgress } from '@/UI/LinearProgress/LinearProgress';
import { I18N_KEYS } from '@/language/keys';
import { useIsHovered } from '@/hooks/useIsHovered';
import ThreeDeeViewer from '../../../Nodes/LazyThreeDeeViewer';
import { DEFAULT_FILE_UPLOAD_HEIGHT, styles } from './FileUploader.styles';

export type UploadPreview = {
  url?: string;
  type?: string;
  mimeType?: string;
  poster?: string;
};

type UploadAreaError = { kind: 'upload' | 'required'; message: string };

const FileUploadOverlay = ({
  isHovered,
  error,
  errorId,
  disabled,
}: {
  isHovered: boolean;
  error?: UploadAreaError;
  errorId?: string;
  disabled: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <Stack.Col
      align="center"
      justify="center"
      gap={8}
      padding={error ? { left: 8, right: 8 } : undefined}
      {...stylex.props(styles.uploadPrompt, isHovered && styles.emptyHoverOverlay)}
    >
      <Icon24Upload aria-hidden {...stylex.props(error ? styles.uploadIconError : disabled && styles.disabledIcon)} />
      <Text.span
        id={error?.kind === 'required' ? errorId : undefined}
        variant="body-medium"
        color={error ? 'danger' : disabled ? 'disabled' : 'default'}
        role={error?.kind === 'required' ? 'alert' : undefined}
      >
        {error?.kind === 'required'
          ? error.message
          : t(error ? I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.UPLOAD_ERROR : I18N_KEYS.GENERAL.UPLOAD)}
      </Text.span>
    </Stack.Col>
  );
};

const MediaElement = ({ media, fitToMedia }: { media: UploadPreview; fitToMedia: boolean }) => {
  const { url, type, mimeType, poster } = media;
  const { t } = useTranslation();

  if (!url) return null;
  if (type === 'video') {
    return (
      <video
        key={url}
        poster={poster}
        draggable={false}
        crossOrigin="anonymous"
        {...stylex.props(styles.visualMedia, fitToMedia && styles.intrinsicVisualMedia)}
        controls
        loop
      >
        <source src={url} type={mimeType} />
      </video>
    );
  }
  if (type === 'image') {
    return (
      <img
        draggable={false}
        src={url}
        alt="Media"
        {...stylex.props(styles.visualMedia, fitToMedia && styles.intrinsicVisualMedia, fitToMedia && styles.fitImage)}
      />
    );
  }
  if (type === 'audio') {
    return <audio draggable="false" crossOrigin="anonymous" src={url} controls />;
  }
  if (type === '3D') {
    return <ThreeDeeViewer objUrl={url} mime={mimeType} />;
  }
  return (
    <Text.span variant="body-medium" color="default">
      {t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.NO_PREVIEW)}
    </Text.span>
  );
};

const ReplaceFileButton = ({
  onClick,
  disabled,
}: {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  disabled: boolean;
}) => {
  const { t } = useTranslation();
  return (
    <ButtonPrimitive onClick={onClick} disabled={disabled} {...stylex.props(!disabled && styles.replaceButton)}>
      <Icon24Refresh aria-hidden {...stylex.props(disabled && styles.disabledIcon)} />
      <Text.span variant="body-medium" color={disabled ? 'disabled' : 'default'}>
        {t(I18N_KEYS.COMMON_COMPONENTS.FILE_UPLOADER.REPLACE)}
      </Text.span>
    </ButtonPrimitive>
  );
};

const DashedBorder = ({ borderColor }: { borderColor: string }) => (
  // Match the FPL variant's dash lengths using SVG rather than a CSS dashed border.
  <svg aria-hidden {...stylex.props(styles.uploadOutline(borderColor))}>
    <rect width="100%" height="100%" rx={4.5} {...stylex.props(styles.uploadOutlineShape)} />
  </svg>
);

const ProgressBar = ({ progress }: { progress: number }) => {
  const { t } = useTranslation();
  return (
    <div {...stylex.props(styles.progress)}>
      <LinearProgress
        variant="determinate"
        value={progress}
        fplProps={{ 'aria-label': t(I18N_KEYS.COMMON_COMPONENTS.PROGRESS.UPLOAD_ARIA) }}
      />
    </div>
  );
};

type FileUploadAreaProps = {
  media: UploadPreview | null;
  progress: number;
  disabled: boolean;
  height?: number;
  error?: UploadAreaError;
  errorId?: string;
  onRequestUpload: () => void;
};

export const FileUploadArea = ({
  media,
  progress,
  disabled,
  height,
  error,
  errorId,
  onRequestUpload,
}: FileUploadAreaProps) => {
  const { isHovered, ...hoverProps } = useIsHovered();
  const isLoading = progress > 0 && progress < 100;
  const fitToMedia = !!media && media.type !== '3D' && height === undefined;
  const borderColor = error
    ? cssVars.color.borderDangerStrong
    : disabled
      ? cssVars.color.borderDisabled
      : cssVars.color.border;

  const handleUpload = (event: React.MouseEvent<HTMLElement>, replace = false) => {
    event.stopPropagation();
    if (!disabled && (!media || replace)) onRequestUpload();
  };

  const areaProps = stylex.props(
    styles.media(height ?? DEFAULT_FILE_UPLOAD_HEIGHT),
    fitToMedia && styles.intrinsicMedia,
    fitToMedia && media.type === 'image' && styles.fitImageFrame,
    media && media.type !== 'audio' && styles.mediaBackground(CHECKERBOARD_BACKGROUND),
    (media || isLoading) && styles.frame(borderColor),
  );

  return (
    <>
      <div {...areaProps} {...hoverProps} onClick={handleUpload}>
        {media && <MediaElement media={media} fitToMedia={fitToMedia} />}
        {isLoading && <ProgressBar progress={progress} />}
        {!media && (
          <FileUploadOverlay isHovered={!disabled && isHovered} error={error} errorId={errorId} disabled={disabled} />
        )}
        {!media && !isLoading && <DashedBorder borderColor={borderColor} />}
      </div>
      {error && (media || error.kind === 'upload') && (
        <Text.span id={errorId} variant="body-medium" color="danger" role="alert">
          {error.message}
        </Text.span>
      )}
      {media && <ReplaceFileButton onClick={(event) => handleUpload(event, true)} disabled={disabled} />}
    </>
  );
};
